/**
 * EmailSignupWidget
 *
 * Homepage email capture form. Visitors enter their email to get
 * weekly specials, new products, and exclusive deals.
 * Submits to POST /api/email-signups (stored in DB, Tammy views in Admin → Email List).
 */

import { useState } from 'react';
import { HiMail, HiCheckCircle } from 'react-icons/hi';

const API_URL = import.meta.env.VITE_API_URL || '';

export default function EmailSignupWidget() {
  const [email, setEmail]   = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || status === 'loading') return;

    setStatus('loading');
    try {
      const res  = await fetch(`${API_URL}/api/email-signups`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: email.trim(), source: 'homepage' }),
      });
      const data = await res.json();

      if (res.ok && data.ok) {
        setStatus('success');
        setMessage(data.message || "You're on the list!");
        setEmail('');
      } else {
        setStatus('error');
        setMessage(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setMessage('Connection error. Please try again.');
    }
  };

  return (
    <section className="bg-gradient-to-r from-violet-700 via-purple-700 to-pink-600 text-white py-14">
      <div className="container-custom">
        <div className="max-w-lg mx-auto text-center">
          <HiMail className="w-10 h-10 mx-auto mb-3 opacity-90" />
          <h2 className="text-2xl md:text-3xl font-display font-bold mb-2">
            Get Weekly Specials
          </h2>
          <p className="text-purple-100 mb-7 text-sm leading-relaxed">
            New seasonal treats, flash sales, and exclusive deals — straight to your inbox.
            No spam, ever. Unsubscribe anytime.
          </p>

          {status === 'success' ? (
            // Success state — friendly confirmation message
            <div className="flex items-center justify-center gap-3 bg-white/20 backdrop-blur-sm rounded-2xl px-6 py-4">
              <HiCheckCircle className="w-6 h-6 flex-shrink-0" />
              <p className="font-semibold text-lg">{message}</p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-3"
              aria-label="Email signup form"
            >
              <label htmlFor="email-signup-input" className="sr-only">
                Email address
              </label>
              <input
                id="email-signup-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 px-4 py-3 rounded-xl text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-white"
                disabled={status === 'loading'}
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="px-6 py-3 bg-white text-purple-700 font-bold rounded-xl hover:bg-purple-50 disabled:opacity-60 text-sm whitespace-nowrap transition-colors"
              >
                {status === 'loading' ? 'Signing up…' : 'Sign Me Up'}
              </button>
            </form>
          )}

          {/* Error message shown below the form */}
          {status === 'error' && (
            <p className="text-red-200 text-sm mt-3">{message}</p>
          )}
        </div>
      </div>
    </section>
  );
}
